import { motion } from "motion/react";
import { Star } from "lucide-react";
import { landingImages } from "@/config/assets";
import { useI18n } from "@/i18n/I18nContext";
import { ImageWithFallback } from "@/landing/components/ImageWithFallback";

const PROBLEM_IMAGES = [
  landingImages.problemChocolate,
  landingImages.problemShampoo,
  landingImages.problemCoffee,
] as const;

const PROBLEM_RATINGS: readonly number[] = [5, 2, 3];

export function ProblemSection() {
  const { messages } = useI18n();
  const p = messages.problems;

  return (
    <section className="bg-[#F7F7F5] py-32">
      <div className="container mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20 text-center"
        >
          <h2 className="mb-6 text-4xl text-[#111111] lg:text-6xl">
            {p.heading}
          </h2>
          <p className="mx-auto max-w-2xl text-xl text-gray-600">
            {p.subheading}
          </p>
        </motion.div>

        <div className="space-y-32">
          {p.items.map((problem, index) => (
            <motion.div
              key={problem.question}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="grid items-center gap-12 lg:grid-cols-2"
            >
              <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                <h3 className="mb-6 text-3xl text-[#111111] lg:text-4xl">
                  {problem.question}
                </h3>

                <div className="inline-block rounded-2xl bg-white p-6 shadow-lg">
                  <div className="mb-3 flex gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star
                        key={i}
                        className={`h-6 w-6 ${
                          i <= (PROBLEM_RATINGS[index] ?? 0)
                            ? "fill-[#FFC857] text-[#FFC857]"
                            : "text-gray-300"
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-lg text-gray-700">{problem.note}</p>
                  <p className="mt-2 text-sm text-[#4CAF50]">{p.notedAgo}</p>
                </div>
              </div>

              <motion.div
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
                className={`relative ${index % 2 === 1 ? "lg:order-1" : ""}`}
              >
                <div className="relative aspect-square overflow-hidden rounded-3xl shadow-2xl">
                  <ImageWithFallback
                    src={PROBLEM_IMAGES[index]}
                    alt={problem.question}
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
